import Phaser from 'phaser';
import { GameMode } from './GameMode.js';

export class TeamVsTeamMode extends GameMode {
  constructor() {
    super('Team vs Team', 'Teams battle for supremacy');
    this.availableTeams = ['red', 'blue', 'green', 'yellow', 'purple'];
    this.teamColors = {
      'red': '#ff4444',
      'blue': '#4444ff', 
      'green': '#44ff44',
      'yellow': '#ffff44',
      'purple': '#ff44ff'
    };
    this.numTeams = 2; // Default to 2 teams, can be set via config
  }

  initialize(scene) {
    // Get team count from scene config if available
    if (scene.teamCount && scene.teamCount >= 2 && scene.teamCount <= 5) {
      this.numTeams = scene.teamCount;
    }
    console.log(`[GameMode] Initialized Team vs Team mode with ${this.numTeams} teams`);
  }

  spawnPlayers(scene, players) {
    // Use configured team count, but ensure we have enough players
    const numTeams = Math.min(this.numTeams, Math.max(2, Math.ceil(players.length / 4))); // At least 4 players per team
    const selectedTeams = this.availableTeams.slice(0, numTeams);
    
    // Distribute players, honoring preferredTeam ('A' -> first team, 'B' -> second team) when present
    players.forEach((player, index) => {
      let teamIndex = index % numTeams;
      if (player.preferredTeam) {
        if (player.preferredTeam === 'A') teamIndex = 0;
        else if (player.preferredTeam === 'B') teamIndex = 1;
      }
      const teamId = selectedTeams[teamIndex];
      const teamColor = this.teamColors[teamId];
      
      player.setTeam(teamId, teamColor);
      
      // Initialize team battle state
      player.teamBattleState = {
        hasEngaged: false, // Track if player has had their first battle
        chargeTarget: null // Initial charge target position
      };
      // Set charging flag for charging bonus on first attack
      player.isCharging = true;
    });

    // Spawn teams in different corners/sides of the map
    const worldBounds = scene.gameManager?.worldBounds || { x: 0, y: 0, width: 2048, height: 1152 };
    const spawnZones = this.getSpawnZones(worldBounds, numTeams);
    const chargeTargets = this.getChargeTargets(worldBounds, numTeams);
    
    selectedTeams.forEach((teamId, teamIndex) => {
      const teamPlayers = players.filter(p => p.team === teamId);
      const spawnZone = spawnZones[teamIndex];
      const chargeZone = chargeTargets[teamIndex];
      
      teamPlayers.forEach(player => {
        const x = Phaser.Math.Between(spawnZone.x, spawnZone.x + spawnZone.width);
        const y = Phaser.Math.Between(spawnZone.y, spawnZone.y + spawnZone.height);
        
        player.sprite.x = x;
        player.sprite.y = y;
        
        // Set initial charge target on opposite side of field
        const chargeX = Phaser.Math.Between(chargeZone.x, chargeZone.x + chargeZone.width);
        const chargeY = Phaser.Math.Between(chargeZone.y, chargeZone.y + chargeZone.height);
        player.targetX = chargeX;
        player.targetY = chargeY;
        player.teamBattleState.chargeTarget = { x: chargeX, y: chargeY };
      });
    });
    
    console.log(`[GameMode] Spawned ${players.length} players in ${numTeams} teams with initial charge targets`);
  }

  getSpawnZones(worldBounds, numTeams) {
    const zones = [];
    const margin = 100;
    const spawnWidth = 300; // Narrow spawn strip width
    
    if (numTeams === 2) {
      // Left side (full height)
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin,
        width: spawnWidth,
        height: worldBounds.height - margin * 2
      });
      // Right side (full height)
      zones.push({
        x: worldBounds.x + worldBounds.width - margin - spawnWidth,
        y: worldBounds.y + margin,
        width: spawnWidth,
        height: worldBounds.height - margin * 2
      });
    } else if (numTeams === 3) {
      // Top, bottom-left, bottom-right
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin,
        width: worldBounds.width - margin * 2,
        height: (worldBounds.height - margin * 3) / 2
      });
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin * 2 + (worldBounds.height - margin * 3) / 2,
        width: (worldBounds.width - margin * 3) / 2,
        height: (worldBounds.height - margin * 3) / 2
      });
      zones.push({
        x: worldBounds.x + margin * 2 + (worldBounds.width - margin * 3) / 2,
        y: worldBounds.y + margin * 2 + (worldBounds.height - margin * 3) / 2,
        width: (worldBounds.width - margin * 3) / 2,
        height: (worldBounds.height - margin * 3) / 2
      });
    } else if (numTeams === 4) {
      // Four corners
      const halfWidth = (worldBounds.width - margin * 3) / 2;
      const halfHeight = (worldBounds.height - margin * 3) / 2;
      
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin,
        width: halfWidth,
        height: halfHeight
      });
      zones.push({
        x: worldBounds.x + margin * 2 + halfWidth,
        y: worldBounds.y + margin,
        width: halfWidth,
        height: halfHeight
      });
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin * 2 + halfHeight,
        width: halfWidth,
        height: halfHeight
      });
      zones.push({
        x: worldBounds.x + margin * 2 + halfWidth,
        y: worldBounds.y + margin * 2 + halfHeight,
        width: halfWidth,
        height: halfHeight
      });
    } else {
      // Five teams - four corners plus center
      const halfWidth = (worldBounds.width - margin * 3) / 2;
      const halfHeight = (worldBounds.height - margin * 3) / 2;
      
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin,
        width: halfWidth,
        height: halfHeight
      });
      zones.push({
        x: worldBounds.x + margin * 2 + halfWidth,
        y: worldBounds.y + margin,
        width: halfWidth,
        height: halfHeight
      });
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin * 2 + halfHeight,
        width: halfWidth,
        height: halfHeight
      });
      zones.push({
        x: worldBounds.x + margin * 2 + halfWidth,
        y: worldBounds.y + margin * 2 + halfHeight,
        width: halfWidth,
        height: halfHeight
      });
      // Center zone for 5th team
      zones.push({
        x: worldBounds.x + worldBounds.width / 2 - halfWidth / 2,
        y: worldBounds.y + worldBounds.height / 2 - halfHeight / 2,
        width: halfWidth,
        height: halfHeight
      });
    }
    
    return zones;
  }

  getChargeTargets(worldBounds, numTeams) {
    // Returns target zones on opposite sides of the field for each team to charge to
    const zones = [];
    const margin = 100;
    const targetWidth = 300; // Width of charge target area
    
    if (numTeams === 2) {
      // Team 0 (left) charges to right side
      zones.push({
        x: worldBounds.x + worldBounds.width - margin - targetWidth,
        y: worldBounds.y + margin,
        width: targetWidth,
        height: worldBounds.height - margin * 2
      });
      // Team 1 (right) charges to left side
      zones.push({
        x: worldBounds.x + margin,
        y: worldBounds.y + margin,
        width: targetWidth,
        height: worldBounds.height - margin * 2
      });
    } else if (numTeams === 3) {
      // For 3+ teams, target the center of the map
      const centerX = worldBounds.x + worldBounds.width / 2;
      const centerY = worldBounds.y + worldBounds.height / 2;
      const centerSize = 400;
      
      for (let i = 0; i < numTeams; i++) {
        zones.push({
          x: centerX - centerSize / 2,
          y: centerY - centerSize / 2,
          width: centerSize,
          height: centerSize
        });
      }
    } else {
      // For 4+ teams, all charge to center
      const centerX = worldBounds.x + worldBounds.width / 2;
      const centerY = worldBounds.y + worldBounds.height / 2;
      const centerSize = 500;
      
      for (let i = 0; i < numTeams; i++) {
        zones.push({
          x: centerX - centerSize / 2,
          y: centerY - centerSize / 2,
          width: centerSize,
          height: centerSize
        });
      }
    }
    
    return zones;
  }

  checkCombat(scene) {
    // Team-aware combat - can't attack teammates
    scene.players.forEach(p => { p.prevBattling = p.isBattling; p.isBattling = false; });
    
    for (let i = 0; i < scene.players.length; i++) {
      for (let j = i + 1; j < scene.players.length; j++) {
        const p1 = scene.players[i];
        const p2 = scene.players[j];
        if (p1.isAlive && p2.isAlive && p1.canAttack(p2)) {
          const dist = Phaser.Math.Distance.Between(p1.sprite.x, p1.sprite.y, p2.sprite.x, p2.sprite.y);
          if (dist < scene.meleeRange) {
            p1.isBattling = true;
            p2.isBattling = true;
            
            // Mark both players as having engaged in their first battle
            if (p1.teamBattleState) p1.teamBattleState.hasEngaged = true;
            if (p2.teamBattleState) p2.teamBattleState.hasEngaged = true;
            // Combat!
            const now = scene.time.now;
            const damage = p1.calculateMeleeDamage();
            if (now > p1.lastAttackTime + scene.getAttackCooldownMs(p1)) {
              p1.lastAttackTime = now;
              if (typeof p1.applyAttackStaminaCost === 'function') {
                p1.applyAttackStaminaCost(2);
              }
              // Clear charging flag after first attack
              if (p1.isCharging) p1.isCharging = false;
              const willDie = p2.health <= damage;
              const actualDamage = p2.takeDamage(damage, { critical: !!p1.lastAttackWasCritical });
              p1.damageDealt += actualDamage;
              if (typeof p1.handleDamageDealtForUltimate === 'function') {
                p1.handleDamageDealtForUltimate(actualDamage);
              }
              scene.createAttackEffect(p1, p2);
              if (scene.sfxManager) {
                scene.sfxManager.onAttack(p1);
                scene.sfxManager.onHit(p2, actualDamage, willDie);
              }
              // Taunt triggers
              if (scene.tauntManager) {
                scene.tauntManager.triggerTaunt(p1, 'attack');
                scene.tauntManager.triggerTaunt(p1, 'hit');
                scene.tauntManager.triggerTaunt(p2, 'hurt');
              }
              if (!p2.isAlive) {
                p1.addElimination();
                scene.createDeathMarker(p2.sprite.x, p2.sprite.y, { popScale: p2.sprite.scale * 10 });
                if (scene.sfxManager) scene.sfxManager.onDeath(p2);
                // Taunt triggers for kill/death
                if (scene.tauntManager) {
                  scene.tauntManager.triggerTaunt(p1, 'killed');
                  scene.tauntManager.triggerTaunt(p2, 'death');
                }
              }
            }

            if (!p1.isAlive || !p2.isAlive) {
              continue;
            }
            if (now > p2.lastAttackTime + scene.getAttackCooldownMs(p2)) {
              p2.lastAttackTime = now;
              if (typeof p2.applyAttackStaminaCost === 'function') {
                p2.applyAttackStaminaCost(2);
              }
              // Clear charging flag after first attack
              if (p2.isCharging) p2.isCharging = false;
              const damage2 = p2.calculateMeleeDamage();
              const willDie = p1.health <= damage2;
              const actualDamage2 = p1.takeDamage(damage2, { critical: !!p2.lastAttackWasCritical });
              p2.damageDealt += actualDamage2;
              if (typeof p2.handleDamageDealtForUltimate === 'function') {
                p2.handleDamageDealtForUltimate(actualDamage2);
              }
              scene.createAttackEffect(p2, p1);
              if (scene.sfxManager) {
                scene.sfxManager.onAttack(p2);
                scene.sfxManager.onHit(p1, actualDamage2, willDie);
              }
              // Taunt triggers
              if (scene.tauntManager) {
                scene.tauntManager.triggerTaunt(p2, 'attack');
                scene.tauntManager.triggerTaunt(p2, 'hit');
                scene.tauntManager.triggerTaunt(p1, 'hurt');
              }
              if (!p1.isAlive) {
                p2.addElimination();
                scene.createDeathMarker(p1.sprite.x, p1.sprite.y, { popScale: p1.sprite.scale * 10 });
                if (scene.sfxManager) scene.sfxManager.onDeath(p1);
                // Taunt triggers for kill/death
                if (scene.tauntManager) {
                  scene.tauntManager.triggerTaunt(p2, 'killed');
                  scene.tauntManager.triggerTaunt(p1, 'death');
                }
              }
            }
          } else if (scene.rangedRange && dist < scene.rangedRange) {
            scene.tryShootArrow(p1, p2, dist);
            scene.tryShootArrow(p2, p1, dist);
          }
        }
      }
    }

    // Post-combat breathing
    if (scene.sfxManager) {
      scene.players.forEach(p => {
        if (p.prevBattling && !p.isBattling && p.isAlive) {
          scene.sfxManager.onBreath(p);
        }
      });
    }
  }

  checkWinCondition(scene) {
    const alivePlayers = scene.players.filter(p => p.isAlive);
    
    if (alivePlayers.length === 0 && scene.gameState === 'playing') {
      console.log('Match ended with no winner - all players died');
      return { winner: null, type: 'draw' };
    }
    
    // Check if only one team remains
    const teamsAlive = new Set();
    alivePlayers.forEach(player => {
      if (player.team) {
        teamsAlive.add(player.team);
      }
    });
    
    if (teamsAlive.size === 1 && scene.gameState === 'playing') {
      const winningTeam = Array.from(teamsAlive)[0];
      const teamPlayers = alivePlayers.filter(p => p.team === winningTeam);
      console.log(`Team ${winningTeam} wins with ${teamPlayers.length} survivors!`);
      return { winner: teamPlayers, type: 'team', team: winningTeam };
    }
    
    return null; // No winner yet
  }

  getPlayerTarget(player, allPlayers) {
    // Team battle behavior:
    // 1. If player hasn't engaged yet, charge to their initial target position
    // 2. Once engaged or at charge target, find and intercept nearest enemy
    
    if (!player.teamBattleState) {
      // Fallback to default behavior if no team state
      return this.findNearestEnemy(player, allPlayers);
    }
    
    // Check if player has reached their charge target
    if (!player.teamBattleState.hasEngaged && player.teamBattleState.chargeTarget) {
      const distToCharge = Phaser.Math.Distance.Between(
        player.sprite.x, 
        player.sprite.y,
        player.teamBattleState.chargeTarget.x,
        player.teamBattleState.chargeTarget.y
      );
      
      // If still far from charge point and not in combat, keep charging
      if (distToCharge > 150 && !player.isBattling) {
        // Return null to signal AI should use existing targetX/targetY (charge point)
        return null;
      }
      
      // Close enough to charge target or currently battling - switch to finding enemies
      player.teamBattleState.hasEngaged = true;
    }
    
    // Find and return nearest enemy to intercept
    return this.findNearestEnemy(player, allPlayers);
  }
  
  findNearestEnemy(player, allPlayers) {
    // Helper method to find nearest enemy (non-teammate)
    let nearestEnemy = null;
    let nearestDistance = Infinity;
    
    allPlayers.forEach(otherPlayer => {
      if (otherPlayer !== player && otherPlayer.isAlive && player.canAttack(otherPlayer)) {
        const dist = Phaser.Math.Distance.Between(player.sprite.x, player.sprite.y, otherPlayer.sprite.x, otherPlayer.sprite.y);
        if (dist < nearestDistance) {
          nearestDistance = dist;
          nearestEnemy = otherPlayer;
        }
      }
    });
    
    return nearestEnemy;
  }
}
